/**
 * The agent's model picker: the chip (maker's mark, model · effort) and the
 * menu that grows from it, with thinking effort pinned under the models. The
 * rail's composer and the strategy start's prompt box both use it, so there is
 * one model menu in the app.
 *
 * Returned as two pieces rather than one element: the menu is glass, and glass
 * nested in the composer's glass cannot blur what is behind it, so each caller
 * places the menu as a sibling of its own surface.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import { canonicalizeChatModelId } from "@/hooks/useChatModels";
import { useChatModelStore } from "@/stores/chatModelStore";
import { cn } from "@/utils/helpers";
import {
  REASONING_LABELS,
  resolveReasoningEffort,
} from "@/app/routes/dashboard/components/agentChatUi";
import { ModelMark } from "./RailParts";
import { ModelMenuBody } from "./ModelMenuBody";

const MENU_EXIT_MS = 120;

export type ModelPickerProps = {
  models: ChatModel[];
  selectedModel: ChatModel | null;
  onModelChange: (model: ChatModel) => void;
  selectedReasoningEffort: ReasoningEffort;
  onReasoningEffortChange: (effort: ReasoningEffort) => void;
  disabled?: boolean;
  /** Extra class on the menu, to place it (the rail floats it over the composer). */
  menuClassName?: string;
};

export function useModelPicker({
  models,
  selectedModel,
  onModelChange,
  selectedReasoningEffort,
  onReasoningEffortChange,
  disabled = false,
  menuClassName,
}: ModelPickerProps) {
  const [menu, setMenu] = useState<"closed" | "open" | "closing">("closed");
  const menuRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const persistedModelId = useChatModelStore((s) => s.modelId);

  /* The pick survives across panels; Auto is the fallback, then the first model. */
  useEffect(() => {
    if (selectedModel || models.length === 0) return;
    const preferred =
      (persistedModelId
        ? models.find((m) => m.id === canonicalizeChatModelId(persistedModelId) && !m.disabled)
        : null) ??
      models.find((m) => m.id === "auto" && !m.disabled) ??
      models.find((m) => !m.disabled && m.id !== "auto") ??
      models[0];
    if (preferred) onModelChange(preferred);
  }, [models, selectedModel, persistedModelId, onModelChange]);

  useEffect(() => {
    if (!selectedModel?.reasoningOptions?.length) return;
    if (selectedModel.reasoningOptions.includes(selectedReasoningEffort)) return;
    onReasoningEffortChange(
      resolveReasoningEffort(selectedModel.reasoningOptions, selectedReasoningEffort),
    );
  }, [selectedModel, selectedReasoningEffort, onReasoningEffortChange]);

  // The exit timer is cancelled when the menu reopens inside it, so a quick
  // second click does not close the menu it just opened.
  const exitTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(exitTimer.current), []);

  const openMenu = useCallback(() => {
    window.clearTimeout(exitTimer.current);
    setMenu("open");
  }, []);

  const closeMenu = useCallback((returnFocus: boolean) => {
    setMenu((state) => (state === "open" ? "closing" : state));
    window.clearTimeout(exitTimer.current);
    exitTimer.current = window.setTimeout(() => setMenu("closed"), MENU_EXIT_MS);
    if (returnFocus) chipRef.current?.focus();
  }, []);

  // Opened from the keyboard, focus lands on the current model; a click leaves
  // it on the chip, so no focus ring flashes on the list.
  const openedByKeyboard = useRef(false);

  useEffect(() => {
    if (menu !== "open") return;
    if (openedByKeyboard.current) {
      menuRef.current
        ?.querySelector<HTMLButtonElement>(".m7r-menu-row[aria-pressed='true']")
        ?.focus();
    } else {
      menuRef.current?.focus({ preventScroll: true });
    }
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || chipRef.current?.contains(target)) return;
      closeMenu(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeMenu(true);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [menu, closeMenu]);

  const pickModel = (model: ChatModel) => {
    if (model.disabled) return;
    onModelChange(model);
    if (
      model.reasoningOptions?.length &&
      !model.reasoningOptions.includes(selectedReasoningEffort)
    ) {
      onReasoningEffortChange(
        model.reasoningOptions.includes("low") ? "low" : model.reasoningOptions[0]!,
      );
    }
  };

  const reasoningOptions = selectedModel?.reasoningOptions ?? [];
  const modelLabel = selectedModel?.name ?? "Model";
  const effortLabel = REASONING_LABELS[selectedReasoningEffort] ?? selectedReasoningEffort;
  const chip = (
    <button
      ref={chipRef}
      type="button"
      className="m7r-chip"
      aria-haspopup="dialog"
      aria-expanded={menu === "open"}
      aria-label={`Model: ${modelLabel}${reasoningOptions.length ? `, thinking effort ${effortLabel}` : ""}`}
      disabled={disabled || models.length === 0}
      onClick={(e) => {
        openedByKeyboard.current = e.detail === 0;
        if (menu === "open") closeMenu(false);
        else openMenu();
      }}
    >
      <ModelMark modelId={selectedModel?.id} />
      {modelLabel}
      {reasoningOptions.length > 0 ? ` · ${effortLabel}` : ""}
      <ChevronDown aria-hidden strokeWidth={1.75} />
    </button>
  );

  const menuElement =
    menu !== "closed" ? (
      <div
        ref={menuRef}
        className={cn("m7r-menu co-chrome-sheet", menuClassName)}
        role="dialog"
        aria-label="Model and thinking effort"
        tabIndex={-1}
        data-closing={menu === "closing" || undefined}
      >
        <ModelMenuBody
          models={models}
          selectedModel={selectedModel}
          onPick={pickModel}
          effort={selectedReasoningEffort}
          onEffortChange={onReasoningEffortChange}
          effortLabelId="m7r-effort-label"
        />
      </div>
    ) : null;

  return { chip, menu: menuElement };
}
